import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { ITEM_BY_SLUG, ITEMS, type PalItem } from "@/lib/items-data";

/**
 * Per-item entity page. Everything visible is generated from the item record,
 * and the FAQPage JSON-LD is built from the exact same strings, so structured
 * data always matches the rendered answer text.
 */
function itemFaq(i: PalItem) {
  const unlock =
    i.unlock > 0
      ? `${i.name} unlocks at technology level ${i.unlock} in Palworld.`
      : `${i.name} has no technology unlock in Palworld — it is gathered or dropped.`;
  return [
    { q: `What level do you unlock ${i.name} in Palworld?`, a: `${unlock} ${i.source}.` },
    { q: `How do you get ${i.name} in Palworld?`, a: `${i.source}. ${i.summary}` },
    { q: `What is ${i.name} used for in Palworld?`, a: `${i.stat}. ${i.tips[0]}` },
    i.ask,
  ];
}

export const Route = createFileRoute("/items/$slug")({
  loader: ({ params }): { item: PalItem } => {
    const item = ITEM_BY_SLUG[params.slug];
    if (!item) throw notFound();
    return { item };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [{ title: "Item not found — Pal Stars" }, { name: "robots", content: "noindex" }],
      };
    const i = loaderData.item;
    // Title stays inside the 60-char SERP budget and leads with the query words
    // people actually type ("palworld assault rifle").
    const suffix = i.category === "Material" || i.category === "Consumable" ? "Uses & Source" : "Recipe & Stats";
    const long = `Palworld ${i.name}: ${suffix}`;
    const title = long.length <= 60 ? long : `Palworld ${i.name}`.slice(0, 60);
    const base = `${i.name} in Palworld — ${i.stat}. ${i.source}.`;
    const desc = base.length <= 155 ? base : base.slice(0, 152).replace(/\s+\S*$/, "") + "…";
    const url = `https://palstars.com/items/${i.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqSchema(itemFaq(i), url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            headline: `Palworld ${i.name} — ${i.category}`,
            description: desc,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            keywords: [
              `palworld ${i.name.toLowerCase()}`,
              `palworld ${i.name.toLowerCase()} recipe`,
              `palworld ${i.category.toLowerCase()}`,
            ].join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://palstars.com/" },
              { "@type": "ListItem", position: 2, name: "Items", item: "https://palstars.com/items" },
              { "@type": "ListItem", position: 3, name: i.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ItemDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That item isn't in our database"
      message="No Palworld item matches this URL. Browse the full item and weapon database to find its recipe, unlock level and best use."
    />
  ),
});

function ItemDetail() {
  const { item: i } = Route.useLoaderData();
  const faq = itemFaq(i);
  const related = ITEMS.filter((x) => x.category === i.category && x.slug !== i.slug).slice(0, 6);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/items" className="hover:text-foreground">
          Items
        </Link>{" "}
        / <span className="text-foreground">{i.name}</span>
      </nav>

      <span className="text-xs font-semibold uppercase tracking-widest text-primary">{i.category}</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld {i.name}</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">{i.summary}</p>

      <dl className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Unlock</dt>
          <dd className="mt-1 font-semibold">
            {i.unlock > 0 ? `Technology level ${i.unlock}` : "No technology unlock (gathered or dropped)"}
          </dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Category</dt>
          <dd className="mt-1 font-semibold">{i.category}</dd>
        </div>
        <div className="glass rounded-xl p-4 sm:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">How to obtain</dt>
          <dd className="mt-1 font-semibold">{i.source}</dd>
        </div>
        <div className="glass rounded-xl p-4 sm:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Key stat</dt>
          <dd className="mt-1 font-semibold">{i.stat}</dd>
        </div>
      </dl>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">How to use {i.name}</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {i.tips.map((t) => (
            <li key={t} className="aeo-answer">
              {t}
            </li>
          ))}
        </ul>
      </section>

      <FaqSection items={faq} heading={`${i.name} FAQ`} />

      {related.length > 0 && (
        <nav aria-label={`Other Palworld ${i.category} entries`} className="glass rounded-2xl p-6 mt-8">
          <h2 className="text-xl font-bold">Other Palworld {i.category.toLowerCase()} entries</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/items/$slug"
                  params={{ slug: r.slug }}
                  className="font-semibold text-primary hover:underline"
                >
                  Palworld {r.name}
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/items" className="text-primary hover:underline">
              Back to the full Palworld items database →
            </Link>
          </p>
        </nav>
      )}
    </div>
  );
}
